import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useLanguage } from '../contexts/LanguageProvider';
import { GoogleLogin } from '@react-oauth/google';
import CloseIcon from './icons/CloseIcon';

interface AuthPageProps { onBack?: () => void; onForgotPassword?: () => void; }

const AuthPage: React.FC<AuthPageProps> = ({ onBack, onForgotPassword }) => {
  const [isLoginView, setIsLoginView] = useState(true);
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCtaIndex, setActiveCtaIndex] = useState<number | null>(null);
  
  // Legal Content Modal State
  const [legalView, setLegalView] = useState<'terms' | 'privacy' | null>(null);
  
  const { login, register, loginWithGoogle, loginWithFacebook } = useAuth();
  const { language, t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isLoginView && (!termsAccepted || !privacyAccepted)) {
        setError("You must accept the Terms and Privacy Policy to create an account.");
        return;
    }

    setIsLoading(true);

    let result;
    if (isLoginView) {
      if (!handle || !password) {
        setError("Please enter your handle and password.");
        setIsLoading(false);
        return;
      }
      result = await login(handle, password);
    } else {
      if (!name || !handle || !email || !password) {
        setError("Please fill out all fields.");
        setIsLoading(false);
        return;
      }
      result = await register(name, handle, email, password);
    }

    if (result && 'error' in result) {
      setError(result.error);
    }
    
    setIsLoading(false);
  };
  
  const handleGoogleSuccess = async (credentialResponse: any) => {
    setIsLoading(true);
    setError('');
    const result = await loginWithGoogle(credentialResponse.credential);
    if (result && 'error' in result) {
        setError(result.error);
    }
    setIsLoading(false);
  };

  const handleFacebookLogin = () => {
    const appId = import.meta.env.VITE_FACEBOOK_APP_ID || '1625097962265482';
    if (!appId) {
      setError('Facebook Login is not configured.');
      return;
    }

    const loadSdkAndLogin = () => {
      (window as any).FB.login((response: any) => {
        if (response.authResponse?.accessToken) {
          setIsLoading(true);
          setError('');
          loginWithFacebook(response.authResponse.accessToken).then((result) => {
            if (result && 'error' in result) setError(result.error);
            setIsLoading(false);
          });
        } else {
          setError('Facebook login was cancelled or failed.');
        }
      }, { scope: 'public_profile,email' });
    };

    if ((window as any).FB) {
      loadSdkAndLogin();
    } else {
      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.onload = () => {
        (window as any).FB.init({ appId, cookie: true, xfbml: false, version: 'v19.0' });
        loadSdkAndLogin();
      };
      document.body.appendChild(script);
    }
  };

  const ctaData = [
    {
      title: "Musician? Performer?",
      desc: "Gig ready? Build your EPK and connect directly with live clubs and festivals.",
      label: "Talent",
      colorClass: "text-brand-orange",
      rotation: "rotate-[-1deg]"
    },
    {
      title: "Venue? Festival?",
      desc: "Fill your calendar. Manage your lineups and flyers with real talents you can scout.",
      label: "Business",
      colorClass: "text-blue-400",
      rotation: "rotate-[2deg]"
    },
    {
      title: "Production? Service?",
      desc: "The backbone of the industry. List your gear, rates, and find your next production.",
      label: "Service",
      colorClass: "text-green-400",
      rotation: "rotate-[1deg]"
    },
    {
      title: "Education? Schools?",
      desc: "Shape the future. Showcase your academy and connect with aspiring talents.",
      label: "Edu",
      colorClass: "text-yellow-400",
      rotation: "rotate-[-2deg]"
    }
  ];

  const toggleCta = (index: number) => {
    setActiveCtaIndex(activeCtaIndex === index ? null : index);
  };

  const renderLegalModal = () => {
    if (!legalView) return null;

    const content = legalView === 'terms' ? (
        language === 'it' ? renderItalianTerms() : renderEnglishTerms()
    ) : (
        language === 'it' ? renderItalianPrivacy() : renderEnglishPrivacy()
    );

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 md:p-10">
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setLegalView(null)}></div>
            <div className="relative bg-brand-surface w-full max-w-4xl h-full max-h-[90vh] rounded-2xl border border-gray-800 shadow-2xl flex flex-col overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-800 bg-brand-surface sticky top-0 z-10">
                    <h2 className="text-2xl font-black text-white uppercase tracking-tighter">
                        {legalView === 'terms' ? t('TERMS_NAV') : t('PRIVACY_NAV')}
                    </h2>
                    <button onClick={() => setLegalView(null)} className="p-2 hover:bg-gray-800 rounded-full text-brand-muted hover:text-white transition-colors">
                        <CloseIcon className="h-6 w-6" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6 md:p-10 prose prose-invert max-w-none custom-scrollbar font-urbanist">
                    {content}
                </div>
                <div className="p-4 border-t border-gray-800 text-center bg-gray-900/50">
                    <button onClick={() => setLegalView(null)} className="bg-brand-orange text-white font-black px-10 py-3 rounded-full uppercase tracking-widest text-xs">
                        {t('BACK')}
                    </button>
                </div>
            </div>
        </div>
    );
  };

  return (
    <div className="bg-brand-dark min-h-screen flex flex-col md:flex-row overflow-x-hidden font-urbanist relative">
      {renderLegalModal()}
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-4 left-4 z-50 flex items-center gap-2 text-sm text-white hover:text-brand-orange transition-colors font-black tracking-wide bg-black/40 backdrop-blur-sm px-3 py-2 rounded-full border border-white/10 hover:border-brand-orange/40"
        >
          <span className="text-base leading-none">←</span> Back to events
        </button>
      )}
      
      <div className="relative w-full md:w-1/2 lg:w-3/5 bg-brand-dark flex flex-col justify-center items-start px-8 py-20 md:px-16 lg:px-24 overflow-hidden min-h-[400px] md:min-h-screen border-r border-gray-800">
        <div className="absolute inset-0 z-0 opacity-80">
           <img 
            src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?q=80&w=2070&auto=format&fit=crop" 
            className="w-full h-full object-cover" 
            alt="Stage with microphone and amplifier" 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-transparent md:hidden" />
        </div>

        <div className="relative z-10 w-full max-w-4xl">
          <div className="mb-6">
            <img src="/logo.png" alt="beYourAMP" className="h-20 w-auto" />
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black leading-tight tracking-tighter">
            <span className="text-white block">{t('MADE_BY_ARTISTS')}</span>
            <span className="text-brand-orange block">{t('FOR_ARTISTS')}</span>
          </h2>
          
          <p className="mt-8 text-lg md:text-xl text-gray-300 tracking-wide max-w-lg">
            The Professional Network for Music & Entertainment.
          </p>

          <div className="grid grid-cols-2 gap-3 md:gap-6 mt-12 md:mt-16 max-w-3xl">
            {ctaData.map((cta, index) => (
              <div 
                key={index}
                onClick={() => toggleCta(index)}
                className={`bg-white/5 backdrop-blur-md p-4 rounded-lg border border-white/10 ${cta.rotation} shadow-2xl cursor-pointer transition-all duration-300 hover:bg-white/10 active:scale-95 group ${activeCtaIndex === index ? 'ring-2 ring-brand-orange/50 scale-[1.02]' : ''}`}
              >
                <h4 className={`${cta.colorClass} font-bold text-[10px] md:text-xs tracking-widest mb-1 uppercase`}>
                  {cta.title}
                </h4>
                
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${activeCtaIndex === index ? 'max-h-40 opacity-100' : 'max-h-0 md:max-h-40 opacity-0 md:opacity-100'}`}>
                  <p className="text-sm md:text-xl font-jersey text-white leading-tight mt-2">
                    "{cta.desc}"
                  </p>
                </div>
                
                <div className="flex justify-between items-center mt-2 md:mt-4">
                  <p className="text-[9px] md:text-[10px] text-brand-muted font-bold tracking-widest uppercase">{cta.label}</p>
                  <div className={`md:hidden transition-transform duration-300 ${activeCtaIndex === index ? 'rotate-180 text-brand-orange' : 'text-brand-muted'}`}>
                    <svg className="h-3 w-3 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full md:w-1/2 lg:w-2/5 flex flex-col items-center justify-start p-8 pt-6 md:pt-16 bg-brand-surface overflow-y-auto">
        <div className="w-full max-w-md -mt-4 md:mt-0">
          <div className="text-center mb-8 md:mb-10">
            <h1 className="text-3xl font-black text-white tracking-tighter uppercase">
              {t('JOIN_YOUR_STAGE')}
            </h1>
          </div>

          <div className="flex flex-col items-center space-y-4 mb-4">
            <div className="w-full shadow-md">
              <GoogleLogin 
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google Login Failed')}
                theme="filled_black"
                shape="pill"
                width="100%"
                text="continue_with"
              />
            </div>
            
            <button onClick={handleFacebookLogin} className="w-full flex items-center justify-center space-x-3 bg-[#1877F2] text-white font-bold py-3 px-4 rounded-full hover:bg-[#166fe5] transition-all shadow-lg">
              <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" /></svg>
              <span className="tracking-widest font-bold">Continue with Facebook</span>
            </button>
          </div>

          <div className="text-[10px] text-center text-brand-muted font-bold uppercase tracking-widest px-4 mb-8">
            By accessing through social login, you acknowledge and agree to beYourAMP's 
            <button onClick={() => setLegalView('terms')} className="text-brand-orange hover:underline mx-1">Terms and Conditions</button> 
            and <button onClick={() => setLegalView('privacy')} className="text-brand-orange hover:underline mx-1">Privacy Policy</button>.
          </div>

          <div className="flex items-center my-8">
            <div className="flex-grow border-t border-gray-700"></div>
            <span className="flex-shrink mx-4 text-brand-muted font-bold text-[10px] tracking-widest uppercase">or use your handle</span>
            <div className="flex-grow border-t border-gray-700"></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLoginView && (
              <input
                type="text"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-gray-700/40 text-white rounded-xl p-4 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all text-sm font-medium"
              />
            )}
            <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-orange font-bold">@</span>
                <input
                    type="text"
                    placeholder="handle"
                    value={handle}
                    onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    className="w-full bg-gray-700/40 text-white rounded-xl p-4 pl-10 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all font-mono text-sm font-medium"
                />
            </div>
            {!isLoginView && (
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-gray-700/40 text-white rounded-xl p-4 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all text-sm font-medium"
              />
            )}
            <div className="space-y-1">
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-gray-700/40 text-white rounded-xl p-4 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-orange transition-all text-sm font-medium"
              />
              {isLoginView && (
                <div className="text-right pt-1">
                  <button type="button" onClick={onForgotPassword} className="text-xs text-brand-muted hover:text-brand-orange transition-colors font-bold tracking-wider uppercase">Forgot password?</button>
                </div>
              )}
            </div>

            {!isLoginView && (
                <div className="space-y-3 py-2">
                    <label className="flex items-start cursor-pointer group">
                        <input 
                            type="checkbox" 
                            checked={termsAccepted} 
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="mt-1 h-4 w-4 bg-gray-700 border-gray-600 rounded text-brand-orange focus:ring-brand-orange" 
                        />
                        <span className="ml-3 text-[11px] font-bold text-gray-300 uppercase tracking-wide leading-tight group-hover:text-white transition-colors">
                            I accept the <button type="button" onClick={() => setLegalView('terms')} className="text-brand-orange hover:underline">Terms and Conditions</button>
                        </span>
                    </label>
                    <label className="flex items-start cursor-pointer group">
                        <input 
                            type="checkbox" 
                            checked={privacyAccepted} 
                            onChange={(e) => setPrivacyAccepted(e.target.checked)}
                            className="mt-1 h-4 w-4 bg-gray-700 border-gray-600 rounded text-brand-orange focus:ring-brand-orange" 
                        />
                        <span className="ml-3 text-[11px] font-bold text-gray-300 uppercase tracking-wide leading-tight group-hover:text-white transition-colors">
                            I accept the <button type="button" onClick={() => setLegalView('privacy')} className="text-brand-orange hover:underline">Privacy and Cookie Policy</button>
                        </span>
                    </label>
                </div>
            )}
            
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 p-3 rounded-lg">
                <p className="text-red-400 text-xs text-center font-bold">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || (!isLoginView && (!termsAccepted || !privacyAccepted))}
              className="w-full bg-brand-orange text-white font-bold py-4 px-4 rounded-full hover:bg-orange-600 active:scale-[0.98] disabled:opacity-50 transition-all text-sm tracking-widest shadow-lg shadow-brand-orange/20 mt-4 uppercase"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
              ) : (
                isLoginView ? t('SIGN_IN') : t('SIGN_UP')
              )}
            </button>
          </form>

          <div className="text-center mt-12 mb-8">
            <button 
              onClick={() => { setIsLoginView(!isLoginView); setError(''); }} 
              className="text-gray-400 hover:text-white transition-colors group"
            >
              <span className="text-sm font-medium">
                {isLoginView ? "Don't have an account?" : "Already have an account?"}
              </span>
              <span className="text-sm font-bold text-brand-orange ml-2 group-hover:underline tracking-widest uppercase">
                {isLoginView ? t('SIGN_UP') : t('SIGN_IN')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- EXPANDED LEGAL CONTENT ---

const renderEnglishTerms = () => (
    <div className="space-y-10">
        <header className="border-b border-gray-800 pb-6">
            <p className="text-lg text-gray-200 leading-relaxed font-semibold">
                Welcome to beYourAMP. This document governs your relationship with beYourAMP s.r.l. and your use of our comprehensive professional networking and management platform for the global entertainment industry.
            </p>
        </header>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">1. Definitions and Preamble</h2>
            <div className="space-y-4 text-gray-300">
                <p><strong>"beYourAMP"</strong> (or "the Application") refers to the website, mobile applications, and services provided by beYourAMP s.r.l.</p>
                <p><strong>"Owner"</strong> refers to beYourAMP s.r.l. , VAT number/Tax code 18397091002, with registered offices in Rome, Italy.</p>
                <p><strong>"Users"</strong> refers to any individual or entity registered on the platform, including Talents, Businesses, Services, and Education providers.</p>
                <p>By registering or using the Application, you agree to be bound by these Conditions. If you do not agree, you must immediately cease all use of the platform.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">2. Services Provided</h2>
            <div className="space-y-4 text-gray-300">
                <p>beYourAMP provides a digital stage for the entertainment industry, offering various tools based on user clusters:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li><strong>Talents:</strong> Digital EPK management, gig applications, and professional networking.</li>
                    <li><strong>Businesses:</strong> Scouting tools, roster management, and event organization.</li>
                    <li><strong>Services:</strong> Technical specification hosting, equipment rental visibility, and service-based matching.</li>
                    <li><strong>Edu:</strong> Promotion of training programs and academy showcases.</li>
                    <li><strong>Fans:</strong> Discovery of live shows and direct support for artists.</li>
                </ul>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">3. User Accounts and Responsibility</h2>
            <div className="space-y-4 text-gray-300">
                <p>You must be at least 18 years old to create a professional account. You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account.</p>
                <p>Users must provide accurate, current, and complete information during the registration process. Impersonation of other professionals or entities is strictly prohibited and will result in account termination.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">4. Subscription Fees and Payments</h2>
            <div className="space-y-4 text-gray-300">
                <p>Access to certain features requires a paid subscription (e.g., Pro or Pro+ tiers). Fees are billed in advance on a monthly or yearly basis.</p>
                <p><strong>Renewals:</strong> Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current billing cycle.</p>
                <p><strong>Refunds:</strong> Subject to mandatory consumer law, digital services already utilized or provided are generally non-refundable.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">5. User Content and License</h2>
            <div className="space-y-4 text-gray-300">
                <p>You retain ownership of the content you upload (photos, videos, text). However, you grant beYourAMP a non-exclusive, worldwide, royalty-free license to host, display, and distribute this content to facilitate networking and platform functionality.</p>
                <p>You represent that you own all necessary rights to any content you post. Prohibited content includes material that is illegal, defamatory, pornographic, or infringes on third-party intellectual property.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">6. Limitation of Liability</h2>
            <div className="space-y-4 text-gray-300">
                <p>beYourAMP acts as an intermediary. We do not guarantee the accuracy of user profiles, the quality of services provided by users, or that a user will successfully secure a gig or employee through the platform.</p>
                <p>The Owner shall not be liable for any indirect, incidental, or consequential damages resulting from your use of the Application or any interactions between users.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">7. Termination</h2>
            <div className="space-y-4 text-gray-300">
                <p>We reserve the right to suspend or terminate your account at any time, without notice, if you violate these conditions or if your conduct harms the community or beYourAMP's reputation.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">8. Governing Law</h2>
            <div className="space-y-4 text-gray-300">
                <p>These terms are governed by and construed in accordance with the laws of Italy. Any disputes shall be subject to the exclusive jurisdiction of the Courts of Rome, without prejudice to consumer protection rights provided by the law of the user's residence.</p>
            </div>
        </section>
    </div>
);

const renderItalianTerms = () => (
    <div className="space-y-10">
        <header className="border-b border-gray-800 pb-6">
            <p className="text-lg text-gray-200 leading-relaxed font-semibold">
                Benvenuto su beYourAMP. Questo documento regola il tuo rapporto con beYourAMP s.r.l. e l'utilizzo della nostra piattaforma professionale per l'industria dell'intrattenimento.
            </p>
        </header>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">1. Definizioni e Premessa</h2>
            <div className="space-y-4 text-gray-300">
                <p><strong>"beYourAMP"</strong> si riferisce al sito web, alle applicazioni e ai servizi forniti da beYourAMP s.r.l.</p>
                <p><strong>"Titolare"</strong> è beYourAMP s.r.l. , P.IVA/Codice Fiscale 18397091002, con sede legale a Roma, Italia.</p>
                <p><strong>"Utenti"</strong> sono i soggetti registrati sulla piattaforma, inclusi Talent, Business, Service, Edu e Fan.</p>
                <p>L'utilizzo dell'Applicazione comporta l'accettazione integrale delle presenti Condizioni. Se non accetti, devi interrompere immediatamente l'uso della piattaforma.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">2. Servizi Offerti</h2>
            <div className="space-y-4 text-gray-300">
                <p>beYourAMP fornisce un palcoscenico digitale per l'industria dell'intrattenimento, offrendo strumenti specifici:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li><strong>Talent:</strong> Gestione EPK, candidature per ingaggi e networking.</li>
                    <li><strong>Business:</strong> Strumenti di scouting, gestione dei roster e organizzazione eventi.</li>
                    <li><strong>Service:</strong> Visibilità per schede tecniche, noleggio attrezzature e matching tecnico.</li>
                    <li><strong>Edu:</strong> Promozione di programmi di formazione e showcase per accademie.</li>
                    <li><strong>Fan:</strong> Scoperta di spettacoli dal vivo e supporto diretto agli artisti.</li>
                </ul>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">3. Account e Responsabilità</h2>
            <div className="space-y-4 text-gray-300">
                <p>Devi avere almeno 18 anni per creare un account professionale. Sei responsabile della riservatezza delle tue credenziali e di ogni attività svolta tramite il tuo account.</p>
                <p>È vietato l'uso di identità false o il furto d'identità di altri professionisti o aziende. Qualsiasi violazione comporterà la chiusura immediata del profilo.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">4. Abbonamenti e Pagamenti</h2>
            <div className="space-y-4 text-gray-300">
                <p>L'accesso ad alcune funzionalità richiede un abbonamento (es. Pro o Pro+). I costi vengono fatturati anticipatamente su base mensile o annuale.</p>
                <p><strong>Rinnovo:</strong> Gli abbonamenti si rinnovano automaticamente a meno di disdetta effettuata almeno 24 ore prima della scadenza.</p>
                <p><strong>Recesso:</strong> Ai sensi della normativa sui consumatori, il diritto di recesso non è applicabile se il servizio è già stato fornito o se il prodotto digitale è già stato scaricato.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">5. Contenuti degli Utenti</h2>
            <div className="space-y-4 text-gray-300">
                <p>Mantieni la proprietà dei contenuti caricati (foto, video, testi). Concedi a beYourAMP una licenza non esclusiva e gratuita per ospitare, mostrare e distribuire tali contenuti per le finalità della piattaforma.</p>
                <p>Dichiari di possedere tutti i diritti sui contenuti che pubblichi. Sono vietati contenuti illegali, diffamatori, pornografici o che violano diritti di proprietà intellettuale altrui.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">6. Limitazione di Responsabilità</h2>
            <div className="space-y-4 text-gray-300">
                <p>beYourAMP agisce come intermediario. Non garantiamo la veridicità dei profili, la qualità dei servizi degli utenti o il successo nell'ottenere ingaggi tramite la piattaforma.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">7. Legge Applicabile</h2>
            <div className="space-y-4 text-gray-300">
                <p>Le presenti condizioni sono regolate dalla legge italiana. Qualsiasi controversia sarà di competenza esclusiva del Foro di Roma, fatti salvi i diritti previsti per gli utenti consumatori.</p>
            </div>
        </section>
    </div>
);

const renderEnglishPrivacy = () => (
    <div className="space-y-10">
        <header className="border-b border-gray-800 pb-6">
            <p className="text-lg text-gray-200 leading-relaxed font-semibold">
                Your privacy is essential to us. This policy describes how beYourAMP s.r.l. collects, uses, and protects your personal data in compliance with the GDPR and other applicable data protection laws.
            </p>
        </header>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">1. Data Controller</h2>
            <div className="space-y-4 text-gray-300">
                <p><strong>beYourAMP s.r.l.</strong>, with registered office in Rome, Italy, is the data controller for the processing of your personal data collected through the website and application.</p>
                <p>Contact: privacy@beyouramp.com</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">2. What Data We Collect</h2>
            <div className="space-y-4 text-gray-300">
                <p>We collect data necessary to provide our networking services:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li><strong>Account Info:</strong> Name, email, stage handle, and password.</li>
                    <li><strong>Professional Data:</strong> Roles, instruments, genres, city of residence, and travel plans.</li>
                    <li><strong>Content:</strong> Media items, bio, technical specs (riders), and social media links.</li>
                    <li><strong>Technical Data:</strong> IP address, device info, and browsing behavior for security and analytics.</li>
                    <li><strong>Payment Data:</strong> Processed via secure third-party providers (Stripe/PayPal); we do not store full credit card numbers.</li>
                </ul>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">3. Purpose of Processing</h2>
            <div className="space-y-4 text-gray-300">
                <p>We process your data for the following reasons:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>To operate the platform and facilitate matches between talents and organizers.</li>
                    <li>To process payments and manage subscriptions.</li>
                    <li>To communicate essential updates and customer support.</li>
                    <li>To improve platform security and prevent fraud.</li>
                    <li>With your consent, for marketing and profiling purposes.</li>
                </ul>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">4. Data Sharing and Transfers</h2>
            <div className="space-y-4 text-gray-300">
                <p>Your data may be shared with IT service providers (hosting, analytics), payment gateways, and legal authorities when mandatory. If data is transferred outside the EEA, we ensure standard contractual clauses are in place.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">5. Data Retention</h2>
            <div className="space-y-4 text-gray-300">
                <p>We keep your data as long as your account is active. Upon deletion, some data may be kept for legal, tax, or administrative requirements for up to 10 years.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">6. Your Rights</h2>
            <div className="space-y-4 text-gray-300">
                <p>Under GDPR, you have the right to:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>Access your data.</li>
                    <li>Rectify incorrect information.</li>
                    <li>Request data erasure ("right to be forgotten").</li>
                    <li>Object to processing or request restriction.</li>
                    <li>Data portability.</li>
                </ul>
                <p>You can exercise these rights by writing to privacy@beyouramp.com.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">7. Cookies</h2>
            <div className="space-y-4 text-gray-300">
                <p>We use essential cookies for login sessions and functional cookies for preferences. We use Cookiebot for consent management. You can adjust your preferences via the "Cookie Settings" link in the footer.</p>
            </div>
        </section>
    </div>
);

const renderItalianPrivacy = () => (
    <div className="space-y-10">
        <header className="border-b border-gray-800 pb-6">
            <p className="text-lg text-gray-200 leading-relaxed font-semibold">
                La tua privacy è fondamentale. Questa informativa descrive come beYourAMP s.r.l. raccoglie, utilizza e protegge i tuoi dati personali in conformità con il GDPR.
            </p>
        </header>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">1. Titolare del Trattamento</h2>
            <div className="space-y-4 text-gray-300">
                <p><strong>beYourAMP s.r.l.</strong>, con sede legale a Roma, Italia, è il titolare del trattamento dei dati personali raccolti tramite il sito e l'applicazione.</p>
                <p>Contatto: privacy@beyouramp.com</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">2. Dati Raccolti</h2>
            <div className="space-y-4 text-gray-300">
                <p>Raccogliamo i dati necessari per fornire i nostri servizi di networking:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li><strong>Info Account:</strong> Nome, email, handle stage e password.</li>
                    <li><strong>Dati Professionali:</strong> Ruoli, strumenti, generi, città e piani di viaggio.</li>
                    <li><strong>Contenuti:</strong> Foto, video, biografia, schede tecniche (rider) e link social.</li>
                    <li><strong>Dati Tecnici:</strong> Indirizzo IP, info dispositivo e dati di navigazione per sicurezza e analisi.</li>
                </ul>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">3. Finalità del Trattamento</h2>
            <div className="space-y-4 text-gray-300">
                <p>Trattiamo i tuoi dati per:</p>
                <ul className="list-disc pl-5 space-y-2">
                    <li>Gestire la piattaforma e facilitare il matching professionale.</li>
                    <li>Gestire abbonamenti e pagamenti.</li>
                    <li>Comunicare aggiornamenti essenziali e supporto tecnico.</li>
                    <li>Garantire la sicurezza ed evitare frodi.</li>
                    <li>Con il tuo consenso, per fini di marketing e profilazione.</li>
                </ul>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">4. Periodo di Conservazione</h2>
            <div className="space-y-4 text-gray-300">
                <p>I dati vengono conservati per tutta la durata dell'account. Dopo la cancellazione, alcuni dati potrebbero essere conservati per obblighi legali fino a 10 anni.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">5. I Tuoi Diritti</h2>
            <div className="space-y-4 text-gray-300">
                <p>Hai il diritto di accedere ai tuoi dati, rettificarli, chiederne la cancellazione, opporsi al trattamento e richiedere la portabilità. Scrivi a privacy@beyouramp.com per esercitare i tuoi diritti.</p>
            </div>
        </section>

        <section>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4 border-b border-gray-800 pb-2">6. Cookie</h2>
            <div className="space-y-4 text-gray-300">
                <p>Utilizziamo cookie tecnici per le sessioni e cookie funzionali. Per la gestione del consenso utilizziamo Cookiebot. Puoi modificare le tue preferenze tramite il link "Cookie Policy" nel footer.</p>
            </div>
        </section>
    </div>
);

export default AuthPage;